/* eslint-disable max-len */
import React, {
  useState, useEffect, useRef, useCallback,
} from 'react'
import { useSelector } from 'react-redux'
import PropTypes from 'prop-types'
import debounce from 'lodash/debounce'
import throttle from 'lodash/throttle'
import ScrollerControls from './ScrollerControls'
import { CardRefresh, AdSlot } from '..'
import styles from './styles'
import { API_IMAGE_OBJECT } from '../../utils/types'

const Scroller = (props) => {
  const {
    assets, specialEvent, programmeTab, liveTab, adSlotProps, recoID, isLive, isAdSlot,
    sponsorship, large, live, page, showCardTitle, isMLT,
    recommendedTab, hasTitle, playIcon, isSponsorship, headerName, groupId,
  } = props
  const users = useSelector(stateSelector => stateSelector.user)
  const innerContainer = useRef(null)
  const outerContainer = useRef(null)
  const [sponsorState, setSponsorState] = useState(null)
  const [posterAdState, setPosterAdState] = useState(null)
  const [layoutReady, setLayoutReady] = useState(false)
  const [state, setState] = useState({
    currentPage: 1,
    totalPages: 1,
    pageDist: 0,
    sponsoredPageDist: 0,
    controlsHeight: null,
    totalItems: assets.length,
  })

  const calculateLayout = useCallback(throttle(() => {
    // this should never happen - should be dealt with by filtering at Group level
    if (assets.length <= 1) return
    // outer wrapper, constrained to page size
    const outerWidth = outerContainer?.current?.offsetWidth
    const innerWidth = innerContainer?.current?.offsetWidth
    if (!outerWidth || !innerWidth) return
    // get the first child item. We can safely assume all cards are the same size
    // (not anymore (sad face))
    const normalItem = innerContainer.current.querySelector('*')
    // sponsored row assumes all items are the width of the first child which is too large.
    // This sets this to the normal item width
    const sponsoredRowItem = innerContainer.current.firstChild
    // Gets height for controls so they sit centred on image
    const controlsHeight = (sponsorState === 'true') ? '345px' : window.getComputedStyle(sponsoredRowItem).getPropertyValue('height')
    const items = isSponsorship ? sponsoredRowItem : normalItem

    // Ensure items have actual width before continuing
    const itemWidth = items.offsetWidth
    if (!itemWidth) return

    const itemMargin = parseInt(window.getComputedStyle(items).getPropertyValue('margin-right'), 10)

    const itemSize = itemWidth + itemMargin // size of one card, including margin
    // how many full items make up a single page. Partial cards belong to the next page
    // Ensure at least 1 item per page to prevent division by zero
    const itemsPerPage = Math.max(1, Math.floor(outerWidth / itemSize))
    const totalItems = sponsorState === 'true' || posterAdState === 'true' ? state.totalItems + 1 : state.totalItems
    const totalPages = Math.max(1, Math.ceil(totalItems / itemsPerPage)) // ensure at least 1 page
    const pageDist = itemsPerPage * itemSize // distance to move per page transition

    setState(prevState => ({
      ...prevState,
      totalPages,
      pageDist,
      sponsoredPageDist: pageDist,
      controlsHeight,
    }))

    const newPage = Math.min(state.currentPage, totalPages)
    goToPage(newPage)
    setLayoutReady(true)
  }, 100), [assets.length, isSponsorship, state.totalItems, sponsorState, posterAdState])

  useEffect(() => {
    if (liveTab) return
    if (programmeTab && adSlotProps) {
      window.googletag.cmd.push(() => {
        window.googletag.pubads().refresh()
      })
    }
  }, [])

  useEffect(() => {
    // Create a resize observer to detect when item dimensions change
    const resizeObserver = new ResizeObserver((entries) => {
      // We only need the first item's width to calculate layout
      const firstItem = innerContainer.current?.querySelector('*')
      if (firstItem?.offsetWidth) {
        calculateLayout()
      }
    })

    // Start observing the first item
    const firstItem = innerContainer.current?.querySelector('*')
    if (firstItem) {
      resizeObserver.observe(firstItem)
    }

    // Add scroll listener to update current page based on scroll position
    const handleScroll = throttle(() => {
      if (!outerContainer.current || !state.pageDist) return
      const scrollPosition = outerContainer.current.scrollLeft
      const currentPage = Math.round(scrollPosition / state.pageDist) + 1
      setState(prevState => ({
        ...prevState,
        currentPage,
      }))
    }, 100)

    outerContainer.current?.addEventListener('scroll', handleScroll)

    // Initial layout calculation
    const layoutTimer = requestAnimationFrame(() => {
      calculateLayout()
    })

    if (programmeTab && adSlotProps) {
      window.googletag.cmd.push(() => {
        window.googletag.pubads().addEventListener('slotRenderEnded', (event) => {
          if (event.slot.getSlotElementId() === 'native-regular') {
            setSponsorState(!event.isEmpty ? 'true' : 'false')
          }
          if (event.slot.getSlotElementId() === 'native-poster') {
            setPosterAdState(!event.isEmpty ? 'true' : 'false')
          }
        })
      })
      // Remove adslot if sponsorState is false
      if (sponsorState === 'false') {
        const adSlotElement = document.getElementById('adslot')
        if (adSlotElement) {
          adSlotElement.parentNode.removeChild(adSlotElement)
          calculateLayout()
        }
      }

      if (posterAdState === 'false') {
        const posterSlotElement = document.getElementById('poster-slot')
        if (posterSlotElement) {
          posterSlotElement.parentNode.removeChild(posterSlotElement)
          calculateLayout()
        }
      }
    }
    const debouncedResize = debounce(calculateLayout, 300)
    window.addEventListener('resize', debouncedResize)

    return () => {
      window.removeEventListener('resize', debouncedResize)
      outerContainer.current?.removeEventListener('scroll', handleScroll)
      cancelAnimationFrame(layoutTimer)
      resizeObserver.disconnect()
    }
  }, [calculateLayout, sponsorState, posterAdState])

  const goToPage = (newPage) => {
    let newScrollPosition = 0
    const {
      currentPage, pageDist, totalPages,
    } = state

    let nextPage = newPage

    const sponsoredRowItem = innerContainer.current.lastChild
    if (isSponsorship) {
      const itemMargin = parseInt(window.getComputedStyle(sponsoredRowItem).getPropertyValue('margin-right'), 10)
      const mainSponsoredImageSize = innerContainer.current.querySelector('*').offsetWidth
      setState(prevState => ({
        ...prevState,
        pageDist: (nextPage === 1 || nextPage === totalPages + 1)
          ? mainSponsoredImageSize - itemMargin
          : state.sponsoredPageDist,
      }))
    }

    if (nextPage === totalPages + 1) {
      nextPage = 1
      newScrollPosition = 0
    } else if (nextPage === 0) {
      nextPage = totalPages
      newScrollPosition = innerContainer.current.scrollWidth - outerContainer.current.offsetWidth
    } else if (currentPage === 1 && totalPages === 1) {
      newScrollPosition = 0
    } else if (nextPage === totalPages) {
      newScrollPosition = innerContainer.current.scrollWidth - outerContainer.current.offsetWidth
    } else {
      newScrollPosition = (nextPage - 1) * pageDist
    }

    outerContainer.current.scrollTo({
      left: newScrollPosition,
      behavior: specialEvent ? 'auto' : 'smooth',
    })

    setState(prevState => ({
      ...prevState,
      currentPage: nextPage,
    }))
  }

  return (
    <div className="scroller-wrapper" style={{ position: 'relative', display: 'flex', flexDirection: 'column' }}>
      {layoutReady && state.totalPages > 1 && (
        <ScrollerControls
          totalPages={state.totalPages}
          currentPage={state.currentPage}
          callback={goToPage}
          hasTitle={hasTitle}
          buttonHeight={state.controlsHeight}
          specialEvent={specialEvent}
          liveTab={liveTab}
          programmeTab={programmeTab}
          recommendedTab={recommendedTab}
        />
      )}
      <div
        /* eslint-disable no-self-compare */
        className="Scroller"
        ref={outerContainer}
        style={{ overflowX: 'scroll' }}
      >
        {programmeTab && adSlotProps && isAdSlot && !users?.currentlySubscribed
          ? (
            <ul
              className="scroll"
              ref={innerContainer}
            >
              {!isMLT
                ? (
                  <li id="adslot" className={`${sponsorState === 'true' ? 'ad-container' : 'no-adslot'}`}>
                    <div className={`${sponsorState === 'true' ? 'ad-label' : ''}`} />
                    <AdSlot type="native-regular" path="summary" programmeOrChannelGuid={adSlotProps.guid} adSlotProps={adSlotProps} />
                  </li>
                ) : (
                  <li id="poster-slot" className={`${posterAdState === 'true' ? 'poster-container' : 'no-adslot'}`}>
                    <div className={`${posterAdState === 'true' ? 'ad-label' : ''}`} />
                    <AdSlot type="native-poster" path="summary" programmeOrChannelGuid={adSlotProps.guid} adSlotProps={adSlotProps} />
                  </li>
                )}
              {programmeTab && !isMLT && assets.map((asset, i) => (
                <CardRefresh
                  key={asset.id}
                  noScale={specialEvent}
                  recoID={recoID}
                  {...asset}
                  index={i}
                  groupId={groupId}
                  headerName={headerName}
                  title={typeof asset.summary !== 'undefined' ? asset.title : asset.label}
                  availableUntil={asset.availableUntil}
                  duration={asset?.duration || ''}
                  summary={typeof asset.summary !== 'undefined' ? asset.summary : asset.title}
                  poster={props.poster}
                  page={page}
                  isLive={props.isLive}
                  programmeTab={programmeTab}
                  showTitle={false}
                  playIcon={playIcon}
                  sportsExclusive={asset.premierSportsExclusive}
                />
              ))}
              {isMLT && assets.map((asset, i) => (
                <CardRefresh
                  sponsorship={sponsorship}
                  key={asset.id}
                  large={large}
                  noScale={specialEvent}
                  recoID={recoID}
                  {...asset}
                  groupId={groupId}
                  headerName={headerName}
                  showTitle={showCardTitle}
                  live={live}
                  index={i}
                  description={asset.description}
                  poster={props.poster}
                  page={page}
                  recommendedTab={recommendedTab}
                />
              ))}
            </ul>
          )
          : (
            <ul
              className="scroll"
              ref={innerContainer}
            >
              {(programmeTab || liveTab) && assets.map((asset, i) => (
                <CardRefresh
                  key={asset.id}
                  noScale={specialEvent}
                  recoID={recoID}
                  {...asset}
                  index={i}
                  groupId={groupId}
                  headerName={headerName}
                  title={typeof asset.summary !== 'undefined' ? asset.title : asset.label}
                  availableUntil={asset.availableUntil}
                  duration={asset?.duration || ''}
                  summary={typeof asset.summary !== 'undefined' ? asset.summary : asset.title}
                  poster={props.poster}
                  page={page}
                  isLive={isLive}
                  liveTab={liveTab}
                  programmeTab={programmeTab}
                  showTitle={false}
                  playIcon={playIcon}
                  sportsExclusive={asset.premierSportsExclusive}
                />
              ))}
              {(!programmeTab && !liveTab) && assets.map((asset, i) => (
                <CardRefresh
                  sponsorship={sponsorship}
                  key={asset.id}
                  large={large}
                  noScale={specialEvent}
                  recoID={recoID}
                  {...asset}
                  groupId={groupId}
                  headerName={headerName}
                  showTitle={showCardTitle}
                  live={live}
                  index={i}
                  description={asset.description}
                  poster={props.poster}
                  page={page}
                  recommendedTab={recommendedTab}
                />
              ))}
            </ul>
          )}
        <style jsx>{styles}</style>
      </div>
    </div>
  )
}

Scroller.propTypes = {
  hasTitle: PropTypes.bool,
  isSponsorship: PropTypes.bool,
  specialEvent: PropTypes.bool,
  assets: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string,
      link: PropTypes.string,
      image: API_IMAGE_OBJECT,
      posters: PropTypes.array,
      subtitle: PropTypes.string,
      label: PropTypes.string,
      overlay: PropTypes.string,
    })
  ),
  sponsorship: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    image: PropTypes.shape({
      id: PropTypes.number,
      _filepath: PropTypes.string,
    }),
  }),
  recoID: PropTypes.string,
  groupId: PropTypes.number,
  headerName: PropTypes.string,
  showTitle: PropTypes.bool,
  large: PropTypes.bool,
  live: PropTypes.bool,
  isLive: PropTypes.bool,
  adSlotProps: PropTypes.object,
  isMLT: PropTypes.bool,
  isAdSlot: PropTypes.bool,
  page: PropTypes.string,
  showCardTitle: PropTypes.bool,
  poster: PropTypes.bool,
  programmeTab: PropTypes.bool,
  liveTab: PropTypes.bool,
  playIcon: PropTypes.bool,
  recommendedTab: PropTypes.bool,
}

Scroller.defaultProps = {
  hasTitle: true,
  isSponsorship: false,
  specialEvent: false,
  assets: [],
  showTitle: false,
  sponsorship: null,
  recoID: '',
  large: false,
  live: false,
  isMLT: false,
  isLive: false,
  adSlotProps: {
    guid: '',
  },
  page: '',
  groupId: 0,
  headerName: '',
  showCardTitle: true,
  isAdSlot: false,
  poster: false,
  programmeTab: false,
  liveTab: false,
  playIcon: false,
  recommendedTab: false,
}

export default Scroller
